import {
  pageRouteObject,
  checkoutProgressDataObject,
  addToCartItemDataObject,
  trackViewItemDataObject,
  removeFromCartItemDataObject,
  addToFavoriteDataObject,
  beginCheckoutDataObject,
  trackPurchaseDataObject,
  trackErrorMessage,
  searchQueryDataObject,
  liveStreamMixPanelDataObject
} from './common';

let mixPanelInitialized = false;

// webview mode
const minimal = new URLSearchParams(window.location.search).get('minimal');

function mixPanelTag(mixPanel, eventName, dataObject) {
  if (minimal === 'true') return;
  if (!process.env.REACT_APP_MIXPANEL_TOKEN) return;
  if (!mixPanelInitialized) {
    mixPanel.init(process.env.REACT_APP_MIXPANEL_TOKEN, { debug: true });
    mixPanelInitialized = true;
  }
  mixPanel.track(eventName, {
    ...dataObject
  });
}

export function mixPanelSetUserId(mixPanel, userId) {
  if (!process.env.REACT_APP_MIXPANEL_TOKEN) return;
  if (!mixPanelInitialized) {
    mixPanel.init(process.env.REACT_APP_MIXPANEL_TOKEN, { debug: true });
    mixPanelInitialized = true;
  }
  mixPanel.identify(String(userId));
}

export function mixPanelTrackPageView(mixPanel, fullRoute) {
  mixPanelTag(mixPanel, 'page_view', pageRouteObject(fullRoute));
}

export function mixPanelTrackAddItemToCart(mixPanel, item, price) {
  mixPanelTag(mixPanel, 'add_to_cart', addToCartItemDataObject(item, price));
}

export function mixPanelTrackRemoveItemFromCart(mixPanel, item, price) {
  mixPanelTag(mixPanel, 'remove_from_cart', removeFromCartItemDataObject(item, price));
}

// The price passed to this function may be null, for one reason or another.
// This could happen in case:
//  1) The user's i18nSettings have not loaded yet, so the view currency is unknown.
//  2) The item is priced on request, so no price is available meaningfully.
export function mixPanelTrackAddToFavorites(mixPanel, item, price) {
  mixPanelTag(mixPanel, 'add_to_wishlist', addToFavoriteDataObject(item, price));
}

// This is called when a user starts the checkout process.
export function mixPanelTrackBeginCheckout(mixPanel, cart) {
  mixPanelTag(mixPanel, 'begin_checkout', beginCheckoutDataObject(cart));
}

// This is called when a user _COMPLETES_ a checkout step.
// Steps should be numbered starting from 1.
// As of August 2, 2020, this means that:
//  - Step 1 is shipping info
//  - Step 2 is payment info
//  - Step 3 is review
export function mixPanelTrackCheckoutProgress(mixPanel, cart, stepNumber, optionName) {
  mixPanelTag(mixPanel, 'checkout_progress', checkoutProgressDataObject(cart, stepNumber, optionName));
}

// This is called when a user logs in to the website.
// This refers to the explicit action of logging in, rather than the action of coming back while already having logged
// in during a previous session.
// loginMethod must be one of:
//  - "google": Logging in with Google
//  - "facebook": Logging in with Facebook
//  - "lemonade": Logging in with a PIPP account.
export function mixPanelTrackLogin(mixPanel, loginMethod) {
  mixPanelTag(mixPanel, 'login', { method: loginMethod });
}

// This is called when a user signs up to the site, including when they login via a third-party service provider
// for the first time. That is, if you login through facebook for the first time, this must be called.
// signUpMethod must be one of:
//  - "google": Logging in with Google
//  - "facebook": Logging in with Facebook
//  - "lemonade": Logging in with a PIPP account.
export function mixPanelTrackSignUp(mixPanel, signUpMethod) {
  mixPanelTag(mixPanel, 'sign_up', { method: signUpMethod });
}

export function mixPanelTrackPurchase(mixPanel, cart, transactionId, i18nSettings, deliveryAddress) {
  mixPanelTag(mixPanel, 'purchase', trackPurchaseDataObject(cart, transactionId, i18nSettings, deliveryAddress));
}

export function mixPanelTrackViewItem(mixPanel, item) {
  mixPanelTag(mixPanel, 'view_item', trackViewItemDataObject(item));
}

export function mixPanelTrackPurchaseFinishError(mixPanel, errorMessage) {
  mixPanelTag(mixPanel, 'place_order_error', trackErrorMessage(errorMessage));
}

export function mixPanelTrackBeginCheckoutError(mixPanel, errorMessage) {
  mixPanelTag(mixPanel, 'begin_checkout_error', trackErrorMessage(errorMessage));
}

export function mixPanelTrackShippingFormError(mixPanel, specificEventName, errorMessage) {
  mixPanelTag(mixPanel, 'shipping_form_error_' + specificEventName, trackErrorMessage(errorMessage));
}

export function mixPanelTrackBillingFormError(mixPanel, specificEventName, errorMessage) {
  mixPanelTag(mixPanel, 'billing_form_error_' + specificEventName, trackErrorMessage(errorMessage));
}

export function mixPanelTrackPaymentFormError(mixPanel, specificEventName, errorMessage) {
  mixPanelTag(mixPanel, 'payment_form_error_' + specificEventName, trackErrorMessage(errorMessage));
}

export function mixPanelTrackPromoCodeError(mixPanel, errorMessage) {
  mixPanelTag(mixPanel, 'promo_code_error', trackErrorMessage(errorMessage));
}

export function mixPanelTrackSearchQuery(mixPanel, query) {
  mixPanelTag(mixPanel, 'search_query', searchQueryDataObject(query));
}

export const mixPanelTrackMissingOption = (mixPanel, errorMessage) => {
  mixPanelTag(mixPanel, 'user_missed_select_option', trackErrorMessage(errorMessage));
};

export const mixPanelTrackMissingSelectSize = (mixPanel, errorMessage) => {
  mixPanelTag(mixPanel, 'user_missed_select_size_measurement', trackErrorMessage(errorMessage));
};

export const mixPanelTrackMissingSelectFabric = (mixPanel, errorMessage) => {
  mixPanelTag(mixPanel, 'user_missed_select_fabric', trackErrorMessage(errorMessage));
};

export const mixPanelRegisterSuperProperties = (mixPanel, properties) => {
  mixPanel.register(properties);
};

export const mixPanelClearSuperProperty = (mixPanel, property) => {
  mixPanel.unregister(property);
};

export const mixPanelTrackLiveStartPlay = (mixPanel, liveStream, user) => {
  mixPanelTag(mixPanel, 'play_live', liveStreamMixPanelDataObject(liveStream, user));
};

export const mixPanelTrackLiveThruPlay = (mixPanel, liveStream, user, liveDuration) => {
  mixPanelTag(mixPanel, 'live_thruplay', { ...liveStreamMixPanelDataObject(liveStream, user), live_duration: liveDuration });
};

export const mixPanelTrackExitLive = (mixPanel, liveStream, user, duration) => {
  mixPanelTag(
    mixPanel,
    'exit_live',
    { ...liveStreamMixPanelDataObject(liveStream, user), exit_at: duration });
};

export const mixPanelTrackLikeLive = (mixPanel, liveStream, user) => {
  mixPanelTag(mixPanel, 'like_live', liveStreamMixPanelDataObject(liveStream, user));
};

export const mixPanelTrackUnLikeLive = (mixPanel, liveStream, user) => {
  mixPanelTag(mixPanel, 'unlike_live', liveStreamMixPanelDataObject(liveStream, user));
};

export const mixPanelTrackShareLive = (mixPanel, liveStream, user) => {
  mixPanelTag(mixPanel, 'share_live', liveStreamMixPanelDataObject(liveStream, user));
};
